/**
 * The deck — every card as a fanned hand, the dashboard's signature moment.
 *
 * The fan itself lives in `CardFan` (shared with the cashback page); this adds
 * the dashboard's part: which card is picked first, and the focused card's facts
 * underneath, whose name links to the card's page.
 *
 * The first card shown is the one with the soonest bill still to pay — the card
 * a user opening the dashboard most likely came to check — not simply the
 * first alphabetically.
 */

import { ArrowUpRight } from "lucide-react";
import * as React from "react";
import { Link } from "react-router-dom";

import { CardFan, type FanItem } from "@/components/cards/CardFan";
import { urgencyLabel } from "@/components/dashboard/DueRunway";
import type { CardSummary } from "@/lib/api/client";
import { cn } from "@/lib/cn";
import { daysUntil, formatDate, formatMoney } from "@/lib/format";

export function WalletFan({ cards, today }: { cards: CardSummary[]; today: string }) {
  const [current, setCurrent] = React.useState<string | null>(() => firstPick(cards, today));
  const n = cards.length;

  const items: FanItem[] = React.useMemo(
    () =>
      cards.map((c) => ({
        id: c.card_id,
        issuer: c.issuer,
        name: c.nickname ?? c.product_name ?? c.issuer,
        last4: c.last4,
        theme: c.theme,
        figure:
          c.outstanding !== null ? { label: "Outstanding", value: formatMoney(c.outstanding) } : null,
      })),
    [cards],
  );

  const focused = cards.find((c) => c.card_id === current) ?? cards[0];
  if (!focused) return null;

  return (
    <div className="flex h-full flex-col">
      <div className="flex items-center justify-between">
        <span className="label-micro">
          Your deck · {n} card{n === 1 ? "" : "s"}
        </span>
        <span className="hidden text-[11px] text-faint sm:inline">Tap a card or a name</span>
      </div>

      <CardFan
        className="mb-6 mt-3 flex-1 justify-center"
        items={items}
        selectedId={focused.card_id}
        onSelect={(id) => id && setCurrent(id)}
        maxCardWidth={268}
        label="Choose a card"
      />

      {/* The focused card's facts. Keyed so they fade when the card changes. */}
      <div
        key={focused.card_id}
        className="animate-rise space-y-4"
        style={{ animationDuration: "0.6s" }}
      >
        <div className="min-w-0">
          <div className="label-micro truncate">{focused.issuer}</div>
          <Link
            to={`/cards/${focused.card_id}`}
            className="group mt-1 inline-flex max-w-full items-baseline gap-2 text-2xl font-medium tracking-[-0.035em] text-ink"
          >
            <span className="truncate">
              {focused.nickname ?? focused.product_name ?? focused.issuer}
            </span>
            <span className="accent-serif text-xl text-muted">•{focused.last4}</span>
            <ArrowUpRight className="h-4 w-4 self-center text-faint transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-ink" />
          </Link>
        </div>
        <dl className="grid grid-cols-3 gap-4 border-t border-hairline pt-4">
          <Fact label="Outstanding" value={focused.outstanding !== null ? formatMoney(focused.outstanding) : "—"} />
          <Fact
            label="Due"
            value={focused.due_date ? formatDate(focused.due_date).slice(0, 6) : "—"}
            note={
              focused.due_date
                ? urgencyLabel(focused.urgency, daysUntil(focused.due_date))
                : focused.statement_count === 0
                  ? "No statement yet"
                  : undefined
            }
            tone={
              focused.urgency === "overdue"
                ? "text-overdue"
                : focused.urgency === "soon" || focused.urgency === "unconfirmed"
                  ? "text-warn"
                  : focused.urgency === "paid"
                    ? "text-paid"
                    : undefined
            }
          />
          <Fact
            label="Available"
            value={focused.available_limit !== null ? formatMoney(focused.available_limit) : "—"}
          />
        </dl>
      </div>
    </div>
  );
}

/** The card with the soonest bill still to pay, else the first card. */
function firstPick(cards: CardSummary[], today: string): string | null {
  const upcoming = cards
    .filter((c) => c.due_date && c.due_date >= today && c.urgency !== "paid")
    .sort((a, b) => (a.due_date! < b.due_date! ? -1 : 1));
  return (upcoming[0] ?? cards[0])?.card_id ?? null;
}

function Fact({
  label,
  value,
  note,
  tone,
}: {
  label: string;
  value: string;
  note?: string;
  tone?: string;
}) {
  return (
    <div className="min-w-0">
      <dt className="text-xs text-faint">{label}</dt>
      <dd className="mt-1 text-[17px] font-medium tracking-[-0.02em] text-ink">{value}</dd>
      {note && <dd className={cn("mt-0.5 text-[11.5px] text-faint", tone)}>{note}</dd>}
    </div>
  );
}
